import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { getRandomPictures } from "services/pictureServices";
import type { RandomPicture } from "types/Pictures";
import { getCurrentLanguage } from "utilities/CurrentLanguageGetter";
import CancelIcon from "assets/icons/CancelIcon";
import Tape1 from "./assets/Tape1";
import Tape2 from "./assets/Tape2";
import Tape3 from "./assets/Tape3";
import Tape4 from "./assets/Tape4";
import Tape5 from "./assets/Tape5";
import Tape6 from "./assets/Tape6";
import Tape7 from "./assets/Tape7";
import Tape8 from "./assets/Tape8";
import PrintIcon from "assets/icons/PrintIcon";
import Polaroid from "components/__reusable/Polaroid";
import useWindowDimensions from "hooks/useWindowDimensions";

const PRINT_DOCUMENT_HEIGHT = 2480;
const PRINT_DOCUMENT_WIDTH = 3508;
const PRINT_PAGE_WIDTH_PX = 1122;
const PRINT_PAGE_HEIGHT_PX = 794;

const waitForImages = (root: HTMLElement | null) => {
  if (!root) return Promise.resolve();
  const images = Array.from(root.querySelectorAll("img"));
  if (images.length === 0) return Promise.resolve();
  return Promise.all(
    images.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete) {
            resolve();
            return;
          }
          const finish = () => resolve();
          img.addEventListener("load", finish, { once: true });
          img.addEventListener("error", finish, { once: true });
        }),
    ),
  ).then(() => undefined);
};

interface MonthlyPicturesDialogProps {
  onClose: () => void;
}

const MonthlyPicturesDialog: React.FC<MonthlyPicturesDialogProps> = ({
  onClose,
}) => {
  const { t } = useTranslation();
  const deviceScreen = useWindowDimensions();
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelButtonContainerRef = useRef<HTMLDivElement>(null);
  const printButtonContainerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [pictures, setPictures] = useState<RandomPicture[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [pictureContainerDimension, setPictureContainerDimension] = useState<
    React.CSSProperties | undefined
  >(undefined);

  const fetchPictures = async () => {
    setErrorMessage("");
    try {
      const pictures = await getRandomPictures(6, getCurrentLanguage());
      setPictures(pictures);
    } catch (e: any) {
      setErrorMessage(e.response.data.error || "_generic._unknown_error");
    }
  };
  useEffect(() => {
    fetchPictures();
  }, []);

  useLayoutEffect(() => {
    const dimension = adjustPictureContainerDimension();
    if (dimension) {
      setPictureContainerDimension(dimension);
    }
  }, [deviceScreen.width, deviceScreen.height]);

  const adjustDialogDimension = () => {
    const aspect = PRINT_DOCUMENT_WIDTH / PRINT_DOCUMENT_HEIGHT;
    if (deviceScreen.width > deviceScreen.height) {
      const maxHeight = deviceScreen.height * 0.9;
      const maxWidth = deviceScreen.width * 0.95;
      const height = maxHeight;
      const width = Math.min(maxWidth, height * aspect);
      return {
        width,
        height,
      };
    }
    const maxWidth = deviceScreen.width * 0.9;
    const maxHeight = deviceScreen.height * 0.9;
    const width = maxWidth;
    const height = Math.min(maxHeight, width / aspect);
    return {
      width,
      height,
    };
  };

  const adjustPictureContainerDimension = () => {
    const dimension = adjustDialogDimension();
    const dialog = dialogRef.current;
    const styles = dialog ? window.getComputedStyle(dialog) : undefined;
    if (!dimension || !styles) return dimension;

    const paddingHorizontal =
      parseInt(styles.paddingLeft) + parseInt(styles.paddingRight);
    const paddingVertical =
      parseInt(styles.paddingTop) + parseInt(styles.paddingBottom);
    const cancelContainerHeight = cancelButtonContainerRef.current
      ? cancelButtonContainerRef.current.clientHeight
      : 0;
    const printContainerHeight = printButtonContainerRef.current
      ? printButtonContainerRef.current.clientHeight
      : 0;

    return {
      width:
        typeof dimension.width === "number"
          ? dimension.width - paddingHorizontal
          : dimension.width,
      height:
        typeof dimension.height === "number"
          ? dimension.height -
            (cancelContainerHeight + printContainerHeight + paddingVertical)
          : dimension.height,
    };
  };

  const getPictureSrc = (picture?: RandomPicture) => {
    if (!picture) return "";
    return picture.thumbnail_picture || "";
  };

  const print = async () => {
    const body = document.body;
    const dialog = dialogRef.current;
    const pictureContainer = containerRef.current;
    if (dialog) {
      const rect = dialog.getBoundingClientRect();
      const computed = window.getComputedStyle(dialog);
      const scale = Math.min(
        1,
        PRINT_PAGE_WIDTH_PX / rect.width,
        PRINT_PAGE_HEIGHT_PX / rect.height,
      );
      dialog.style.setProperty("--print-scale", `${scale}`);
      dialog.style.setProperty("--print-width", `${rect.width}px`);
      dialog.style.setProperty("--print-height", `${rect.height}px`);
      dialog.style.setProperty("--print-font-size", computed.fontSize);
      if (pictureContainer) {
        const containerFontSize =
          window.getComputedStyle(pictureContainer).fontSize;
        dialog.style.setProperty(
          "--print-container-font-size",
          containerFontSize,
        );
      }
    }
    const fontsReady = document.fonts?.ready ?? Promise.resolve();
    const waitForRender = () =>
      new Promise<void>((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
      );
    await waitForRender();
    const assetsReady = waitForImages(dialog || pictureContainer);
    await Promise.all([
      fontsReady.catch(() => null),
      assetsReady.catch(() => null),
    ]);

    body.classList.add("monthly-pictures-printing");
    const cleanup = () => {
      body.classList.remove("monthly-pictures-printing");
      if (dialog) {
        dialog.style.removeProperty("--print-scale");
        dialog.style.removeProperty("--print-width");
        dialog.style.removeProperty("--print-height");
        dialog.style.removeProperty("--print-font-size");
        dialog.style.removeProperty("--print-container-font-size");
      }
    };
    window.addEventListener("afterprint", cleanup, { once: true });
    requestAnimationFrame(() => {
      window.print();
    });
  };

  const modal = (
    <div className="monthly-pictures-overlay fixed z-50 top-0 left-0 w-full h-full bg-black/50 flex justify-center items-center">
      <div
        ref={dialogRef}
        className="monthly-pictures-dialog p-5 bg-white z-50"
        style={adjustDialogDimension()}
      >
        <div className="flex flex-col h-full w-full">
          <div
            ref={cancelButtonContainerRef}
            className="monthly-pictures-cancel-controls h-[5%] w-full flex justify-end"
          >
            <button onClick={onClose}>
              <CancelIcon width={24} height={24} color={"black"} />
            </button>
          </div>
          <div
            ref={containerRef}
            className="monthly-pictures-container w-full h-full relative m-auto"
            style={{
              ...pictureContainerDimension,
              fontSize: "1.2cqw",
            }}
          >
            <div className="w-full h-full absolute top-0 left-[50%] -translate-x-[50%] z-10 pointer-events-auto">
              {errorMessage && (
                <div className="w-full h-full flex items-center justify-center">
                  <p className="text-3xl font-semibold text-center text-white">
                    {errorMessage}
                  </p>
                </div>
              )}
              {!errorMessage && (
                <div className="w-full h-full flex flex-wrap">
                  <div className="w-full h-[50%] flex justify-between">
                    <div className="w-[25%] h-full m-auto p-1 relative rotate-12 flex flex-col justify-center">
                      <div className="w-full relative max-h-full">
                        <Polaroid
                          imgSrc={getPictureSrc(pictures[0])}
                          disableComment={false}
                          fit="cover"
                        />
                        <div className="w-[50%] absolute top-0 -translate-y-[30%] left-0 -translate-x-[25%]">
                          <Tape1 />
                        </div>
                      </div>
                    </div>
                    <div className="w-[25%] h-full m-auto p-1 relative flex flex-col justify-center">
                      <div
                        className={`w-full relative ${
                          pictures[1] ? "max-h-full" : "h-full"
                        }`}
                      >
                        <Polaroid
                          imgSrc={getPictureSrc(pictures[1])}
                          disableComment={false}
                          fit="cover"
                        />
                        <div className="w-[50%] absolute top-0 -translate-y-[30%] left-0 -translate-x-[25%]">
                          <Tape2 />
                        </div>
                      </div>
                    </div>
                    <div className="w-[25%] h-full m-auto p-1 relative -rotate-[17deg] flex flex-col justify-center">
                      <div
                        className={`w-full relative ${
                          pictures[2] ? "max-h-full" : "h-full"
                        }`}
                      >
                        <Polaroid
                          imgSrc={getPictureSrc(pictures[2])}
                          disableComment={false}
                          fit="cover"
                        />
                        <div className="w-[30%] absolute top-0 -translate-y-[30%] left-0 -translate-x-[25%]">
                          <Tape3 />
                        </div>
                        <div className="w-[50%] absolute top-0 -translate-y-[30%] right-0 translate-x-[25%]">
                          <Tape4 />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="w-full h-[50%] flex justify-between">
                    <div className="w-[25%] h-full m-auto p-1 relative -rotate-12 flex flex-col justify-center">
                      <div
                        className={`w-full relative ${
                          pictures[3] ? "max-h-full" : "h-full"
                        }`}
                      >
                        <Polaroid
                          imgSrc={getPictureSrc(pictures[3])}
                          disableComment={false}
                          fit="cover"
                        />
                        <div className="w-[30%] absolute top-0 -translate-y-[30%] left-[50%] -translate-x-[50%]">
                          <Tape5 />
                        </div>
                      </div>
                    </div>
                    <div className="w-[25%] h-full m-auto p-1 relative flex flex-col justify-center">
                      <div
                        className={`w-full relative ${
                          pictures[4] ? "max-h-full" : "h-full"
                        }`}
                      >
                        <Polaroid
                          imgSrc={getPictureSrc(pictures[4])}
                          disableComment={false}
                          fit="cover"
                        />
                        <div className="w-[30%] absolute top-0 -translate-y-[30%] left-0 -translate-x-[25%]">
                          <Tape6 />
                        </div>
                        <div className="w-[40%] absolute top-0 -translate-y-[30%] right-0 translate-x-[25%]">
                          <Tape7 />
                        </div>
                      </div>
                    </div>
                    <div className="w-[25%] h-full m-auto p-1 relative rotate-12 flex flex-col justify-center">
                      <div
                        className={`w-full relative ${
                          pictures[5] ? "max-h-full" : "h-full"
                        }`}
                      >
                        <Polaroid
                          imgSrc={getPictureSrc(pictures[5])}
                          disableComment={false}
                          fit="cover"
                        />
                        <div className="w-[50%] absolute top-0 -translate-y-[40%] left-[50%] -translate-x-[50%] -rotate-12">
                          <Tape8 />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div
            ref={printButtonContainerRef}
            className="monthly-pictures-print-controls h-[5%] w-full flex justify-end space-x-3 relative z-20"
          >
            <button
              className="h-fit bg-[#499F9940] text-primary font-semibold text-sm px-2 py-1 rounded-md flex items-center space-x-2 transition-colors duration-200 ease-out hover:bg-[#499F9955]"
              onClick={print}
            >
              <p>{t("_home._print")}</p>
              <PrintIcon width={20} height={20} color={"#499F99"} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(modal, document.body);
};

export default MonthlyPicturesDialog;
